import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordField from '../components/PasswordField';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import NECSLogo from '../components/NECSLogo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 3-step forgot-password flow:
//   1. Email entry  → server issues an SMS OTP (with email backup)
//   2. Code + new password
//   3. Success → link back to /login
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email | verify | done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Iter-108.3 · Platform-wide password polish state.
  const [revealBoth, setRevealBoth] = useState(false);
  const [pastedNotice, setPastedNotice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testMode, setTestMode] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Unable to submit request right now.');
      // Note: legacy `google_only` short-circuit removed — every account is now
      // eligible for OTP-based password set/reset so users can add email/password
      // as a second sign-in method on top of Google.
      setTestMode(!!data.test_mode);
      setStep('verify');
      toast.success('If an account exists for that email, a 6-digit code is on its way.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Invalid or expired code.');
      setStep('done');
      toast.success('Password reset. Please sign in.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
      style={{
        backgroundImage:
          "linear-gradient(rgba(45,45,45,0.55), rgba(45,45,45,0.65)), url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/fc6q13kd_90226.png')",
      }}
      data-testid="forgot-password-page"
    >
      <Card className="w-full max-w-md p-8 bg-black/80 backdrop-blur-md shadow-2xl border border-white/10" data-testid="forgot-password-form">
        <div className="flex justify-center mb-6">
          <NECSLogo variant="header" size="md" align="center" dataTestId="forgot-password-logo" />
        </div>

        <h2
          className="text-3xl font-bold text-center mb-2 text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em' }}
        >
          Reset Password
        </h2>

        {step === 'email' ? (
          <>
            <p className="text-center text-sm text-zinc-300 mb-6">
              Enter your email — we&apos;ll text a 6-digit code to the phone number on your account
              (with an email backup).
            </p>
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-white font-semibold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email-input"
                  className="bg-transparent border-white/30 text-white placeholder:text-zinc-400 focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
                disabled={loading}
                data-testid="forgot-password-submit-button"
              >
                {loading ? 'Sending…' : 'Send Security Code'}
              </Button>
            </form>
          </>
        ) : step === 'verify' ? (
          <>
            <p className="text-center text-sm text-zinc-300 mb-4">
              We texted a 6-digit code to the phone on file for{' '}
              <span className="text-[#FFD60A] font-semibold">{email}</span>. It expires in 10 minutes.
            </p>
            {testMode && (
              <div
                className="rounded-md border border-amber-600 bg-amber-950/30 text-amber-200 text-xs px-3 py-2 mb-4"
                data-testid="forgot-password-test-mode-note"
              >
                Twilio isn&apos;t configured yet — the code was written to the server logs. Ask an admin
                or check `/var/log/supervisor/backend.err.log` for the code.
              </div>
            )}
            <form onSubmit={submitNewPassword} className="space-y-4">
              <div>
                <Label htmlFor="code" className="text-white font-semibold">Security Code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                  data-testid="forgot-password-code-input"
                  className="bg-transparent border-white/30 text-white text-center tracking-[0.5em] text-lg focus-visible:ring-[#FFD60A]"
                />
              </div>
              <PasswordField
                label="New Password"
                required
                minLength={8}
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                showStrength
                onPasteWarning={setPastedNotice}
                reveal={revealBoth}
                onToggleReveal={() => setRevealBoth((v) => !v)}
                testId="forgot-password-new-password"
              />
              <PasswordField
                label="Confirm Password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={setConfirmPassword}
                autoComplete="new-password"
                matchValue={newPassword}
                onPasteWarning={setPastedNotice}
                reveal={revealBoth}
                onToggleReveal={() => setRevealBoth((v) => !v)}
                testId="forgot-password-confirm-password"
              />
              {pastedNotice && (
                <p
                  className="text-[10px] font-bold px-2 py-1.5 rounded"
                  style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)' }}
                  data-testid="forgot-password-paste-guard"
                >
                  ⚠️ You pasted content that looks like an email or a company name. Passwords should never contain your identity.
                </p>
              )}
              <Button
                type="submit"
                className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
                disabled={loading}
                data-testid="forgot-password-verify-submit"
              >
                {loading ? 'Resetting…' : 'Reset Password'}
              </Button>
              <button
                type="button"
                className="w-full text-xs text-zinc-400 hover:text-[#FFD60A] transition mt-1"
                onClick={() => setStep('email')}
                data-testid="forgot-password-back-to-email"
              >
                ← Use a different email
              </button>
            </form>
          </>
        ) : (
          <div
            className="rounded-md border p-4 text-sm text-zinc-100 text-center"
            style={{ borderColor: '#C9A962', backgroundColor: 'rgba(201,169,98,0.1)' }}
            data-testid="forgot-password-success"
          >
            Password reset! Redirecting you to sign in…
          </div>
        )}

        <div className="text-center mt-6">
          <Link
            to="/login"
            className="text-sm text-zinc-300 hover:text-[#FFD60A] hover:underline transition"
            data-testid="forgot-password-back-to-login"
          >
            ← Back to Login
          </Link>
        </div>
      </Card>
    </div>
  );
}
